<template>
  <v-navigation-drawer
    v-model="drawer"
    rail
    :persistent="mobile ? false : true"
    expand-on-hover
    color="grey-darken-4"
  >
    <v-list density="compact" nav>
      <!-- Home link (always visible) -->
      <v-list-item prepend-icon="mdi-home" title="Home" @click="router.push('/home')"></v-list-item>

      <!-- Accounting: Sales, Petty Cash (each shown only if user has that specific permission) -->
      <v-list-item v-if="hasSales" title="Sales" @click="goToSales">
        <template v-slot:prepend>
          <v-icon color="blue">mdi-chart-bar</v-icon>
        </template>
      </v-list-item>
      <v-list-item v-if="hasPettyCash" title="Petty Cash" @click="goToPettyCash">
        <template v-slot:prepend>
          <v-icon color="green-darken-3">mdi-cash-multiple</v-icon>
        </template>
      </v-list-item>

      <!-- Marketing ( Campaigns links to /marketing which shows all accessible marketing apps) -->
      <v-list-item
        v-if="hasCampaigns"
        title="Marketing"
        @click="goToMarketing"
      >
        <template v-slot:prepend>
          <v-icon color="orange">mdi-bullhorn</v-icon>
          <v-tooltip activator="parent" location="bottom">Marketing</v-tooltip>
        </template>
      </v-list-item>

      <!-- Menu (renamed from Food Cost) -->
      <v-list-item v-if="hasMenu" title="Menu" @click="goToMenu">
        <template v-slot:prepend>
          <v-icon color="indigo">mdi-silverware-fork-knife</v-icon>
        </template>
      </v-list-item>

      <!-- Warehouse (store inventory) -->
      <v-list-item v-if="hasWarehouse" title="Inventory" @click="goToWarehouse">
        <template v-slot:prepend><v-icon color="green-darken-3">mdi-warehouse</v-icon></template>
      </v-list-item>

      <v-list-item v-if="hasJournal" title="Journal" @click="goToJournal">
        <template v-slot:prepend><v-icon color="teal">mdi-book-open-page-variant</v-icon></template>
      </v-list-item>
      <v-list-item v-if="hasJournal" title="Ledger" @click="goToLedger">
        <template v-slot:prepend><v-icon color="teal">mdi-book-open-variant</v-icon></template>
      </v-list-item>
      <v-list-item v-if="isAdmin" title="COA" @click="goToCoa">
        <template v-slot:prepend><v-icon color="teal">mdi-file-tree</v-icon></template>
      </v-list-item>

      <v-list-item
        v-if="hasAssets"
        prepend-icon="mdi-folder-multiple-image"
        title="Assets"
        @click="goToAssets"
      />
      <v-list-item v-if="hasHr" title="HR and Contacts" @click="goToHr">
        <template v-slot:prepend>
          <v-icon color="red-lighten-2">mdi-account-box</v-icon>
        </template>
      </v-list-item>

      <!-- Documentation (visible to all — sections are permission-gated on the page) -->
      <v-list-item prepend-icon="mdi-book-open-variant" title="Documentation" @click="goToDocs" />

      <v-divider v-if="isAppAdmin" class="my-2"></v-divider>

      <!----- audit logs (appadmin only) --------->
      <v-list-item
        v-if="isAppAdmin"
        prepend-icon="mdi-history"
        title="Audit Logs"
        @click="goToAuditLogs"
      ></v-list-item>

      <!----- users (appadmin only) --------->
      <v-list-item
        v-if="isAppAdmin"
        prepend-icon="mdi-account-group"
        title="Users"
        @click="goToUsers"
      ></v-list-item>

      <!----- settings (appadmin only) --------->
      <v-list-item
        v-if="isAppAdmin"
        prepend-icon="mdi-cog"
        title="Settings"
        @click="goToSettings"
      ></v-list-item>
    </v-list>

    <template v-slot:append>
      <v-divider></v-divider>
      <v-list density="compact" nav>
        <v-list-item :title="userDisplayName" @click="goToAccount">
          <template v-slot:prepend><v-icon icon="mdi-account"></v-icon></template>
          <template v-slot:subtitle>
            <span class="user-email">{{ user?.email }}</span>
            <v-tooltip activator="parent" location="bottom">{{ user?.email }}</v-tooltip>
          </template>
        </v-list-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";

const { user, drawer, currentApp } = useApp();
const router = useRouter();
const { mobile } = useDisplay();

// Handle both string (SQLite) and array (PostgreSQL) formats
const getAllowedApps = () => {
  const apps = user.value?.allowed_apps;
  if (!apps) return [];
  if (Array.isArray(apps)) return apps;
  try {
    return JSON.parse(apps);
  } catch {
    return [];
  }
};

// Per-service visibility — each nav item shown only if user has that specific permission
const hasSales = computed(
  () => isAppAdmin.value || ["sales", "accounting"].some((a) => getAllowedApps().includes(a)),
);
const hasPettyCash = computed(
  () => isAppAdmin.value || ["pettycash", "accounting"].some((a) => getAllowedApps().includes(a)),
);
const hasCampaigns = computed(() => isAppAdmin.value || getAllowedApps().includes("campaigns"));
const hasReviews = computed(() => isAppAdmin.value || getAllowedApps().includes("reviews"));
const hasAssets = computed(() => isAppAdmin.value || getAllowedApps().includes("assets"));
const hasHr = computed(
  () => isAppAdmin.value || ["hr", "hr-management"].some((a) => getAllowedApps().includes(a)),
);
const hasMenu = computed(() => isAppAdmin.value || getAllowedApps().includes("menu"));
const hasWarehouse = computed(() => isAppAdmin.value || getAllowedApps().includes("warehouse"));
const hasJournal = computed(() => isAppAdmin.value || getAllowedApps().includes("journal"));
const isAppAdmin = computed(() => user.value?.role === "appadmin");
const isAdmin = computed(() => ["admin", "appadmin"].includes(user.value?.role));
const userDisplayName = computed(() => {
  const name = [user.value?.first_name, user.value?.last_name].filter(Boolean).join(" ");
  return name || user.value?.email;
});

const goToSales = () => {
  router.push("/accounting/sales");
  currentApp.value = "accounting";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToPettyCash = () => {
  router.push("/accounting/petty-cash");
  currentApp.value = "accounting";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToReviews = () => {
  router.push("/reviews");
  currentApp.value = "marketing";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToAssets = () => {
  router.push("/assets");
  currentApp.value = "assets";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToMarketing = () => {
  router.push("/marketing");
  currentApp.value = "marketing";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToHr = () => {
  router.push("/hr");
  currentApp.value = "hr";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToMenu = () => {
  router.push("/menu");
  currentApp.value = "menu";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToWarehouse = () => {
  router.push("/accounting/warehouse");
  currentApp.value = "warehouse";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToJournal = () => {
  router.push("/accounting/journal");
  currentApp.value = "journal";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToLedger = () => {
  router.push("/accounting/ledger");
  currentApp.value = "journal";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToCoa = () => {
  router.push("/accounting/coa");
  currentApp.value = "journal";
  localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
};

const goToAccount = () => {
  router.push("/account");
};

const goToAuditLogs = () => {
  router.push("/settings/audit-logs");
};

const goToUsers = () => {
  router.push("/settings/users");
};

const goToSettings = () => {
  router.push("/settings");
};

const goToDocs = () => {
  router.push("/settings/docs");
};
</script>
